import { ReactElement, useCallback, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stack, Tooltip } from '@mui/material';
import { Visibility as ShowIcon, Edit as EditIcon } from '@mui/icons-material';

import { ItemExtended } from '~services/item/types';
import { useAppSelector } from '~hooks/useAppSelector';
import { selectAuth } from '~store/slices/auth';
import { isOnlyReadUser, isOnlyCreatorOrder } from '~utils/user';
import { replaceParamsPath } from '~utils/router';
import { PATHS } from '~constants/paths';

import { useDashboardVariantV2GiverContentInPreparationTabContext } from '../../Context';
import { ITEM_KEY_ID } from '../../utils';

import { DashboardVariantV2GiverContentInPreparationTabDataTableIconButton } from './styles';

export interface DashboardVariantV2GiverContentInPreparationTabDataTableActionsProps {
  data: ItemExtended;
}

const DashboardVariantV2GiverContentInPreparationTabDataTableActions = ({
  data,
}: DashboardVariantV2GiverContentInPreparationTabDataTableActionsProps): ReactElement => {
  const navigate = useNavigate();
  const { user } = useAppSelector(selectAuth);

  const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);
  const isUserOnlyCreatorOrder = useMemo(
    () => isOnlyCreatorOrder(user),
    [user],
  );

  const {
    dialogs: {
      detailsOrder: { handleOpen: handleOpenDialogDetailsOrder },
    },
  } = useDashboardVariantV2GiverContentInPreparationTabContext();

  const handleShow = useCallback(() => {
    handleOpenDialogDetailsOrder({
      orderId: data[ITEM_KEY_ID],
      currentTab: null,
    });
  }, [data, handleOpenDialogDetailsOrder]);

  const enabledEdit = useMemo(
    () => !isUserOnlyReader && !isUserOnlyCreatorOrder && data.canModify,
    [isUserOnlyReader, isUserOnlyCreatorOrder, data.canModify],
  );

  const handleEdit = useCallback(() => {
    navigate(
      replaceParamsPath(PATHS.ORDERS.EDIT, { ':orderId': data[ITEM_KEY_ID] }),
    );
  }, [data, navigate]);

  return (
    <Stack direction="row" spacing={0.75} justifyContent="center">
      <Tooltip title="Ver" arrow>
        <DashboardVariantV2GiverContentInPreparationTabDataTableIconButton
          color="success"
          variant="outlined"
          onClick={handleShow}
        >
          <ShowIcon />
        </DashboardVariantV2GiverContentInPreparationTabDataTableIconButton>
      </Tooltip>

      {enabledEdit && (
        <Tooltip title="Editar" arrow>
          <DashboardVariantV2GiverContentInPreparationTabDataTableIconButton
            color="info"
            variant="outlined"
            onClick={handleEdit}
          >
            <EditIcon />
          </DashboardVariantV2GiverContentInPreparationTabDataTableIconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

export default memo(
  DashboardVariantV2GiverContentInPreparationTabDataTableActions,
);
